import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import setDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/SetDynamicFormFieldValue";
import {
    DynamicFormButtonShapes,
    DynamicFormButtonShapesPill,
    DynamicFormButtonShapesRectangle,
    DynamicFormButtonShapesRounded,
    DynamicFormButtonTypes,
    DynamicFormButtonTypesOutline,
    DynamicFormButtonTypesSolid,
    DynamicFormButtonVariants,
    DynamicFormButtonVariantsPrimary,
    DynamicFormButtonVariantsSecondary,
    DynamicFormButtonVariantsTertiary,
    DynamicFormEmailRegExp,
    DynamicFormTextFieldLabelPositionsTop,
    DynamicFormThirdPartyApiTypes,
    DynamicFormThirdPartyApiTypesCustom,
} from "@midland-industries/Types/DynamicFormModels";
import DynamicForm from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import DynamicFormTextArea from "@midland-industries/Widgets/DynamicForms/DynamicFormTextArea";
import DynamicFormTextField from "@midland-industries/Widgets/DynamicForms/DynamicFormTextField";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum marketType {
    industrial = "Industrial",
    plumbing = "Plumbing",
    waterworks = "Waterworks",
}

const enum userAuthStatus {
    authenticated = "Authenticated",
    guest = "Guest",
    anonymous = "Anonymous",
}

const enum fields {
    formVariant = "formVariant",
    emailRecipients = "emailRecipients",
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
    buttonVariant = "buttonVariant",
    buttonShape = "buttonShape",
    buttonType = "buttonType",
    showResetButton = "showResetButton",
    enableRecaptcha = "enableRecaptcha",
    sendToThirdPartyApi = "sendToThirdPartyApi",
    sendToApiType = "sendToApiType",
    customApiEndpoint = "customApiEndpoint",
    endpointRequiresAuth = "endpointRequiresAuth",
    endpointUserId = "endpointUserId",
    endpointPassword = "endpointPassword",
    displayCustomApiResponse = "displayCustomApiResponse",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.formVariant]: string;
        [fields.emailRecipients]: string;
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
        [fields.buttonVariant]: DynamicFormButtonVariants;
        [fields.buttonShape]: DynamicFormButtonShapes;
        [fields.buttonType]: DynamicFormButtonTypes;
        [fields.showResetButton]: boolean;
        [fields.enableRecaptcha]: boolean;
        [fields.sendToThirdPartyApi]: boolean;
        [fields.sendToApiType]: DynamicFormThirdPartyApiTypes;
        [fields.customApiEndpoint]: string;
        [fields.endpointRequiresAuth]: boolean;
        [fields.endpointUserId]: string;
        [fields.endpointPassword]: string;
        [fields.displayCustomApiResponse]: boolean;
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms;
    const billToId = state.context.session.billToId;
    const billToState = getBillToState(state, billToId);
    return {
        dynamicFormData,
        authStatus: state.context.session.isAuthenticated
            ? state.context.session.isGuest
                ? userAuthStatus.guest
                : userAuthStatus.authenticated
            : userAuthStatus.anonymous,
        billToId,
        billToState,
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MarketContactUsFormStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    headingWrapper?: InjectableCss;
    headingText?: TypographyPresentationProps;
    container?: GridContainerProps;
    fieldGridItem?: GridItemProps;
    fieldWrapper?: InjectableCss;
    fieldWrapperNoPadding?: InjectableCss;
}

export const marketContactUsFormStyles: MarketContactUsFormStyles = {
    wrapper: {
        css: css`
            width: 100%;
            background-color: ${getColor("primary")};
            border-radius: 25px;
            padding: 25px;
            margin-bottom: 30px;
        `,
    },
    innerWrapper: {},
    headingWrapper: {
        css: css`
            width: 100%;
            margin-bottom: 20px;
            display: flex;
            justify-content: center;
        `,
    },
    headingText: {
        color: "common.accent",
        css: css`
            font-size: 22px;
            font-weight: 600;
            text-transform: uppercase;
        `,
    },
    container: {
        gap: 0,
    },
    fieldGridItem: {
        width: 12,
    },
    fieldWrapper: {
        css: css`
            width: 100%;
            padding-bottom: 10px;
            margin-bottom: 10px;
        `,
    },
    fieldWrapperNoPadding: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = marketContactUsFormStyles;

const MarketContactUsForm: FC<Props> = props => {
    const { id, fields, dynamicFormData, authStatus, billToState } = props;
    const { setDynamicFormFieldValue } = props;
    const {
        formVariant,
        emailRecipients,
        submitButtonText,
        successMessage,
        buttonVariant,
        buttonShape,
        buttonType,
        enableRecaptcha,
        sendToThirdPartyApi,
        sendToApiType,
        customApiEndpoint,
        endpointRequiresAuth,
        endpointUserId,
        endpointPassword,
        displayCustomApiResponse,
    } = fields;

    const formName = `Market ContactUs Form - ${  formVariant}`;

    useEffect(() => {
        if (authStatus === userAuthStatus.authenticated && billToState && billToState.value) {
            // This will trigger for an authenticated user when the BillTo is loaded.
            // Update any form field values that can use defaults.
            // For unauthenticated and guest users, these fields will stay empty.
            setDynamicFormFieldValue({
                formName,
                formField: {
                    key: "b_name",
                    group: "",
                    label: "Company",
                    isRequired: false,
                    value: billToState.value.companyName,
                    type: "DynamicFormTextField",
                    sendOnlyIfChecked: false,
                    hasError: false,
                    error: "",
                },
            });
        }
    }, [billToState]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.innerWrapper}>
                <StyledWrapper {...styles.headingWrapper}>
                    <Typography {...styles.headingText}>Contact Us Today!</Typography>
                </StyledWrapper>
                <DynamicForm
                    id={id}
                    fields={{
                        formName,
                        emailRecipients,
                        submitButtonText,
                        successMessage,
                        buttonVariant,
                        buttonShape,
                        buttonType,
                        showResetButton: false,
                        enableRecaptcha,
                        sendToThirdPartyApi,
                        sendToApiType,
                        customApiEndpoint,
                        endpointRequiresAuth,
                        endpointUserId,
                        endpointPassword,
                        displayCustomApiResponse,
                    }}
                >
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.fieldGridItem}>
                            <StyledWrapper {...styles.fieldWrapper}>
                                <DynamicFormTextField
                                    labelColorWhite={true}
                                    fields={{
                                        key: "nameFirst",
                                        labelPosition: DynamicFormTextFieldLabelPositionsTop,
                                        label: "First Name",
                                        hintText: "First Name",
                                        isRequired: true,
                                        enableMinLength: false,
                                        minLength: 0,
                                        enableMaxLength: false,
                                        maxLength: 100,
                                        validateAsPhoneNumber: false,
                                        validateAsEmailAddress: false,
                                    }}
                                />
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <StyledWrapper {...styles.fieldWrapper}>
                                <DynamicFormTextField
                                    labelColorWhite={true}
                                    fields={{
                                        key: "nameLast",
                                        labelPosition: DynamicFormTextFieldLabelPositionsTop,
                                        label: "Last Name",
                                        hintText: "Last Name",
                                        isRequired: true,
                                        enableMinLength: false,
                                        minLength: 0,
                                        enableMaxLength: false,
                                        maxLength: 100,
                                        validateAsPhoneNumber: false,
                                        validateAsEmailAddress: false,
                                    }}
                                />
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <StyledWrapper {...styles.fieldWrapper}>
                                <DynamicFormTextField
                                    labelColorWhite={true}
                                    fields={{
                                        key: "email",
                                        labelPosition: DynamicFormTextFieldLabelPositionsTop,
                                        label: "Email",
                                        hintText: "Email",
                                        isRequired: true,
                                        enableMinLength: false,
                                        minLength: 0,
                                        enableMaxLength: false,
                                        maxLength: 100,
                                        validateAsPhoneNumber: false,
                                        validateAsEmailAddress: true,
                                    }}
                                />
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <StyledWrapper {...styles.fieldWrapper}>
                                <DynamicFormTextField
                                    labelColorWhite={true}
                                    fields={{
                                        key: "phone",
                                        labelPosition: DynamicFormTextFieldLabelPositionsTop,
                                        label: "Phone",
                                        hintText: "Direct Line",
                                        isRequired: true,
                                        enableMinLength: false,
                                        minLength: 0,
                                        enableMaxLength: false,
                                        maxLength: 100,
                                        validateAsPhoneNumber: true,
                                        validateAsEmailAddress: false,
                                    }}
                                />
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <StyledWrapper {...styles.fieldWrapper}>
                                <DynamicFormTextField
                                    labelColorWhite={true}
                                    fields={{
                                        key: "b_name",
                                        labelPosition: DynamicFormTextFieldLabelPositionsTop,
                                        label: "Company",
                                        hintText: "Company Name",
                                        isRequired: true,
                                        enableMinLength: false,
                                        minLength: 0,
                                        enableMaxLength: false,
                                        maxLength: 100,
                                        validateAsPhoneNumber: false,
                                        validateAsEmailAddress: false,
                                    }}
                                />
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <StyledWrapper {...styles.fieldWrapperNoPadding}>
                                <DynamicFormTextArea
                                    labelColorWhite={true}
                                    fields={{
                                        key: "comments",
                                        labelPosition: DynamicFormTextFieldLabelPositionsTop,
                                        label: "How can we serve you?",
                                        hintText: "Description",
                                        isRequired: true,
                                        enableMaxLength: false,
                                        maxLength: 10000,
                                        rows: 3,
                                    }}
                                />
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                </DynamicForm>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const formTab = {
    displayName: "Form",
    sortOrder: 0,
};

const buttonTab = {
    displayName: "Button",
    sortOrder: 1,
};

const actionTab = {
    displayName: "Actions",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MarketContactUsForm),
    definition: {
        group: "Basic",
        displayName: "Market Contact Us Form",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.formVariant,
                displayName: "Select Market Variant",
                editorTemplate: "DropDownField",
                defaultValue: marketType.industrial,
                fieldType: "General",
                options: [
                    {
                        displayName: "Industrial",
                        value: marketType.industrial,
                    },
                    {
                        displayName: "Plumbing",
                        value: marketType.plumbing,
                    },
                    {
                        displayName: "Waterworks",
                        value: marketType.waterworks,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: formTab,
                sortOrder: 1,
            },
            {
                name: fields.emailRecipients,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                regularExpression: DynamicFormEmailRegExp,
                tab: formTab,
                sortOrder: 2,
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
                tab: formTab,
                sortOrder: 3,
            },
            {
                name: fields.showResetButton,
                displayName: "Include Reset Button",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 4,
                tab: formTab,
            },
            {
                name: fields.enableRecaptcha,
                displayName: "Include ReCaptcha",
                tooltip: "Enabling this setting adds a ReCaptcha element to the form which must be clicked to submit.",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 4,
                tab: formTab,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Submit",
                tab: buttonTab,
                sortOrder: 11,
            },
            {
                name: fields.buttonVariant,
                displayName: "Select Button Variant",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormButtonVariantsPrimary,
                fieldType: "General",
                options: [
                    {
                        displayName: "Primary",
                        value: DynamicFormButtonVariantsPrimary,
                    },
                    {
                        displayName: "Secondary",
                        value: DynamicFormButtonVariantsSecondary,
                    },
                    {
                        displayName: "Tertiary",
                        value: DynamicFormButtonVariantsTertiary,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 12,
            },
            {
                name: fields.buttonShape,
                displayName: "Select Button Shape",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormButtonShapesRectangle,
                fieldType: "General",
                options: [
                    {
                        displayName: "Rectangle",
                        value: DynamicFormButtonShapesRectangle,
                    },
                    {
                        displayName: "Pill",
                        value: DynamicFormButtonShapesPill,
                    },
                    {
                        displayName: "Rounded",
                        value: DynamicFormButtonShapesRounded,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 13,
            },
            {
                name: fields.buttonType,
                displayName: "Select Button Type",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormButtonTypesSolid,
                fieldType: "General",
                options: [
                    {
                        displayName: "Solid",
                        value: DynamicFormButtonTypesSolid,
                    },
                    {
                        displayName: "Outline",
                        value: DynamicFormButtonTypesOutline,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 14,
            },
            {
                name: fields.sendToThirdPartyApi,
                displayName: "Upload FormData to 3rd Party System",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 200,
                tab: actionTab,
            },
            {
                name: fields.sendToApiType,
                displayName: "Select 3rd Party System Type",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormThirdPartyApiTypesCustom,
                fieldType: "General",
                options: [
                    {
                        displayName: "Custom API Endpoint",
                        value: DynamicFormThirdPartyApiTypesCustom,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                isVisible: widget => widget.fields.sendToThirdPartyApi,
                tab: actionTab,
                sortOrder: 210,
            },
            {
                name: fields.customApiEndpoint,
                displayName: "Custom API Endpoint URL",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget =>
                    widget.fields.sendToThirdPartyApi &&
                    widget.fields.sendToApiType === DynamicFormThirdPartyApiTypesCustom,
                tab: actionTab,
                sortOrder: 220,
            },
            {
                name: fields.endpointRequiresAuth,
                displayName: "Upload Requires Authentication",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                isVisible: widget => widget.fields.sendToThirdPartyApi,
                tab: actionTab,
                sortOrder: 221,
            },
            {
                name: fields.endpointUserId,
                displayName: "User Id",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.endpointRequiresAuth,
                tab: actionTab,
                sortOrder: 222,
            },
            {
                name: fields.endpointPassword,
                displayName: "Password",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.endpointRequiresAuth,
                tab: actionTab,
                sortOrder: 223,
            },
            {
                name: fields.displayCustomApiResponse,
                displayName: "Display results from API below Form for testing",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 230,
                tab: actionTab,
            },
        ],
    },
};

export default widgetModule;
